const SPAN_ID_SEPARATOR = ':';

/** FNV-1a: a pure function of the input bytes, no seeding or object identity, so the same text digests identically across sessions, machines, and re-renders. */
const FNV_OFFSET_BASIS_32 = 0x811c9dc5;
const FNV_PRIME_32 = 0x01000193;
/** Base 36 keeps the digest short, since the id is sent to the model on every span. */
const DIGEST_RADIX = 36;

const digestText = (text: string): string => {
  const hash = Array.from(text).reduce(
    (acc, char) => Math.imul(acc ^ char.charCodeAt(0), FNV_PRIME_32) >>> 0,
    FNV_OFFSET_BASIS_32,
  );

  return hash.toString(DIGEST_RADIX);
};

/**
 * Folds the span's own text into the id because the start index alone doesn't survive a protection change: protecting any word
 * but the first still leaves a shorter span starting at index 0, which an index-only id would hand a translation produced for
 * words it no longer contains.
 */
export const buildTranslationSpanId = (elementId: string, startWordIndex: number, text: string): string =>
  [elementId, startWordIndex, digestText(text)].join(SPAN_ID_SEPARATOR);

export type ParsedTranslationSpanId = {
  elementId: string;
  startWordIndex: number;
  textDigest: string;
};

/**
 * Splits from the right, not the left: `elementId` today comes from OCR region indexing and never
 * contains the separator, but nothing enforces that, and a left split would misparse the day it does.
 */
export const parseTranslationSpanId = (spanId: string): ParsedTranslationSpanId | null => {
  const digestSeparatorIndex = spanId.lastIndexOf(SPAN_ID_SEPARATOR);

  if (digestSeparatorIndex === -1) {
    return null;
  }

  const indexSeparatorIndex = spanId.lastIndexOf(SPAN_ID_SEPARATOR, digestSeparatorIndex - 1);

  if (indexSeparatorIndex === -1) {
    return null;
  }

  const elementId = spanId.slice(0, indexSeparatorIndex);
  const startWordIndexText = spanId.slice(indexSeparatorIndex + 1, digestSeparatorIndex);
  const startWordIndex = Number(startWordIndexText);
  const textDigest = spanId.slice(digestSeparatorIndex + 1);
  // `Number('')` is 0, so the empty string must be rejected explicitly before the integer check.
  const hasValidStartWordIndex = startWordIndexText !== '' && Number.isInteger(startWordIndex) && startWordIndex >= 0;

  if (!elementId || !textDigest || !hasValidStartWordIndex) {
    return null;
  }

  return { elementId, startWordIndex, textDigest };
};
